import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';
import '../contracts/MensagensProgramadas_GetBulkProgressResponse.ts';
import '../contracts/MensagensProgramadas_GetBulkProgressResponse';
import '../contracts/MensagensProgramadasLote_BuscaDadosInput';
import '../contracts/MensagensProgramadasLote_BuscadadosResponse';
import '../contracts/MensagensProgramadasLote_BuscarMensagemProgramadaLoteResponse';
import '../contracts/MensagensProgramadasLote_GetFilteredCustomersInput';
import '../contracts/MensagensProgramadasLote_GetFilteredCustomersResponse';
import '../contracts/MensagensProgramadasLote_ScheduleBulkMessagesInput';
import type { MensagensProgramadasLote_ScheduleBulkMessagesInput } from '../contracts/MensagensProgramadasLote_ScheduleBulkMessagesInput';
import '../contracts/MensagensProgramadasLote_ScheduleBulkMessagesResponse';

/**
 * Composable for MensagensProgramadasLote (Bulk Scheduled Messages) operations
 * Provides methods to interact with the Bulk Scheduled Messages API
 */
export function useMensagensProgramadasLote() {
  const isLoading = ref(false);
  const error = ref(null);
  let pollingInterval: any = null;

  /**
   * Fetch bulk operations data for table display
   * Uses filtro_model for date range, franchise, and user filtering
   */
  const fetchBulkOperations = async () => {
    log('useMensagensProgramadasLote', 'fetchBulkOperations', 'Fetching bulk operations');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas_lote/buscaDados', {}, {
        inputContract: 'MensagensProgramadasLote_BuscaDadosInput',
        responseContract: 'MensagensProgramadasLote_BuscadadosResponse'
      });
      log('useMensagensProgramadasLote', 'fetchBulkOperations', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao carregar operações em lote');
      }
    } catch (err) {
      log('useMensagensProgramadasLote', 'fetchBulkOperations', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get filtered customers based on criteria
   * @param {Array} franquiaIds - Array of franchise IDs
   * @param {String} dataInicio - Start date (YYYY-MM-DD)
   * @param {String} dataFim - End date (YYYY-MM-DD)
   * @param {Number} quantidade - Number of customers to retrieve
   */
  const getFilteredCustomers = async (franquiaIds: number[], dataInicio: string, dataFim: string, quantidade: number) => {
    log('useMensagensProgramadasLote', 'getFilteredCustomers', 'Fetching filtered customers', {
      franquiaIds,
      dataInicio,
      dataFim,
      quantidade
    });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas_lote/get_filtered_customers', {
        franquia_ids: franquiaIds,
        data_inicio: dataInicio,
        data_fim: dataFim,
        quantidade: quantidade
      }, {
        inputContract: 'MensagensProgramadasLote_GetFilteredCustomersInput',
        responseContract: 'MensagensProgramadasLote_GetFilteredCustomersResponse'
      });
      log('useMensagensProgramadasLote', 'getFilteredCustomers', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar clientes filtrados');
      }
    } catch (err) {
      log('useMensagensProgramadasLote', 'getFilteredCustomers', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Schedule bulk messages
   * @param {Array} messages - Array of message objects
   * @param {String} currentDatetime - Current datetime in ISO 8601 format
   * @param {Object} bulkData - Bulk operation metadata
   * @param {Array} customersData - Array of customer data for audit trail
   */
  const scheduleBulkMessages = async (
    messages: MensagensProgramadasLote_ScheduleBulkMessagesInput['messages'],
    currentDatetime: MensagensProgramadasLote_ScheduleBulkMessagesInput['current_datetime'],
    bulkData: MensagensProgramadasLote_ScheduleBulkMessagesInput['bulk_data'],
    customersData: MensagensProgramadasLote_ScheduleBulkMessagesInput['customers_data']
  ) => {
    log('useMensagensProgramadasLote', 'scheduleBulkMessages', 'Scheduling bulk messages', {
      messages,
      currentDatetime,
      bulkData,
      customersData
    });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas_lote/schedule_bulk_messages', {
        messages,
        current_datetime: currentDatetime,
        bulk_data: bulkData,
        customers_data: customersData
      }, {
        inputContract: 'MensagensProgramadasLote_ScheduleBulkMessagesInput',
        responseContract: 'MensagensProgramadasLote_ScheduleBulkMessagesResponse'
      });
      log('useMensagensProgramadasLote', 'scheduleBulkMessages', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao agendar mensagens em lote');
      }
    } catch (err) {
      log('useMensagensProgramadasLote', 'scheduleBulkMessages', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get single bulk operation by ID
   * @param {Number} bulkId - ID of the bulk operation
   */
  const getBulkOperationById = async (bulkId: number) => {
    log('useMensagensProgramadasLote', 'getBulkOperationById', 'Fetching bulk operation', bulkId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/mensagens_programadas_lote/buscar_mensagem_programada_lote/${bulkId}`, {
        responseContract: 'MensagensProgramadasLote_BuscarMensagemProgramadaLoteResponse'
      });
      log('useMensagensProgramadasLote', 'getBulkOperationById', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar operação em lote');
      }
    } catch (err) {
      log('useMensagensProgramadasLote', 'getBulkOperationById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch progress data for a specific bulk operation
   * @param {Number} loteId - ID of the bulk operation
   */
  const fetchBulkProgress = async (loteId: number) => {
    log('useMensagensProgramadasLote', 'fetchBulkProgress', 'Fetching bulk progress', loteId);
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/mensagens_programadas/get_bulk_progress/${loteId}`, {
        responseContract: 'MensagensProgramadas_GetBulkProgressResponse'
      });
      log('useMensagensProgramadasLote', 'fetchBulkProgress', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar progresso do lote');
      }
    } catch (err) {
      log('useMensagensProgramadasLote', 'fetchBulkProgress', 'Error', err);
      error.value = err.message;
      throw err;
    }
  };

  /**
   * Start polling for bulk operation updates
   * Emits 'mensagens-programadas-lote.refresh' every 10 seconds
   */
  const startPolling = () => {
    if (pollingInterval) {
      return;
    }
    log('useMensagensProgramadasLote', 'startPolling', 'Starting polling every 10 seconds');
    pollingInterval = setInterval(() => {
      log('useMensagensProgramadasLote', 'startPolling', 'Polling tick - emitting refresh');
      eventBus.emit('mensagens-programadas-lote.refresh');
    }, 10000);
  };

  /**
   * Stop polling for bulk operation updates
   */
  const stopPolling = () => {
    if (pollingInterval) {
      log('useMensagensProgramadasLote', 'stopPolling', 'Stopping polling');
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  return {
    isLoading,
    error,
    fetchBulkOperations,
    getFilteredCustomers,
    scheduleBulkMessages,
    getBulkOperationById,
    fetchBulkProgress,
    startPolling,
    stopPolling
  };
}
